import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    ConfiguratorI18nCacheService, ConfiguratorNewsletterService, EngineTagService,
    FieldTable,
} from '@tj/tj-dmp-lib-api';

@Injectable({
    providedIn: 'root',
})
export class FilterGetterListService {
    constructor(protected service: ConfiguratorI18nCacheService,
                protected engineTagService: EngineTagService,
                protected translate: TranslateService,
                protected newsletterService: ConfiguratorNewsletterService) {}

    public async getFilterListType(tenantCode: string,
                                   type: FieldTable.CategoryFieldEnum,
                                   value?: string,
                                   commandTable?: string,
                                   partner?: string,
                                   language?: string): Promise<{ code: string; value: string }[]> {
        const items = await this.resolveListType(tenantCode, type, value, commandTable, partner, language);

        // Les référentiels (AIRLINE, AIRPORT, CITY, COUNTRY...) contiennent des entrées
        // sans libellé : sans ce filtrage elles s'affichent comme des lignes vides,
        // sélectionnables mais illisibles, dans les selects.
        return (items ?? [])
            .filter(item => item && item.code !== null && item.code !== undefined)
            .map(item => ({ code: String(item.code), value: `${item.value ?? ''}`.trim() }))
            .filter(item => item.value.length > 0);
    }

    private async resolveListType(tenantCode: string,
                                  type: FieldTable.CategoryFieldEnum,
                                  value?: string,
                                  commandTable?: string,
                                  partner?: string,
                                  language?: string): Promise<{ code: string; value: string }[]> {
        switch (type) {
            case 'IATA_CITY':
                return this.service.searchUsingGET('CITY', false, null, value).toPromise().then((res) => res.map(e => ({code: e.key, value: e.value})));
            case 'IATA_AIRPORT':
                return this.service.searchUsingGET('AIRPORT', false, null, value).toPromise().then((res) => res.map(e => ({code: e.key, value: e.value})));
            case 'LANG':
                return [
                    { code: 'fr', value: 'Français' },
                    { code: 'en', value: 'Anglais'},
                    { code: 'de', value: 'Allemand'},
                    { code: 'it', value: 'Italien'},
                    { code: 'es', value: 'Espagnol'},
                    { code: 'nl', value: 'Néerlandais'},
                ];
            case 'IATA_COUNTRY':
            case 'COUNTRY':
                return this.service.searchUsingGET('COUNTRY', false, null, value).toPromise().then((res) => res.map(e => ({code: e.key, value: e.value})));
            case 'CIVILITY':
                return [
                    { code: 'MR', value: this.translate.instant('fields.values.civility.mr') },
                    { code: 'MRS', value: this.translate.instant('fields.values.civility.mrs') },
                ];
            case 'CARRIER':
                return this.service.searchUsingGET('AIRLINE', false, null, value).toPromise().then((res) => res.map(e => ({code: e.key, value: e.value})));
            case 'TAG':
                return this.engineTagService.getAllTags(tenantCode, true).toPromise().then((res) => res.map(e => ({code: e.id.toString(), value: e.name})));
            case 'ACTIVITY_STATUS':
                return [
                    { code: 'ACTIVE', value: this.translate.instant('fields.values.activity_status.active') },
                    { code: 'INACTIVE', value: this.translate.instant('fields.values.activity_status.inactive') },
                    { code: 'DORMANT', value: this.translate.instant('fields.values.activity_status.dormant') }
                ];
            case 'LOYALTY_STATUS':
                return [
                    { code: 'LOYAL', value: this.translate.instant('fields.values.loyalty_status.loyal') },
                    { code: 'NON-LOYAL', value: this.translate.instant('fields.values.loyalty_status.non-loyal') }
                ];
            case 'PURCHASE_STATUS':
                return [
                    { code: 'ACTIVE', value: this.translate.instant('fields.values.purchase_status.active') },
                    { code: 'INACTIVE', value: this.translate.instant('fields.values.purchase_status.inactive') },
                    { code: 'DORMANT', value: this.translate.instant('fields.values.purchase_status.dormant') }
                ];
            case 'CHANNEL':
                return [
                    { code : 'sms', value: 'SMS'},
                    { code : 'email', value : 'EMAIL'}
                ]
            case 'PRODUCT_TYPE':
                switch (commandTable) {
                    case 'flight':
                        return [
                            { code: 'FLIGHT', value: this.translate.instant('fields.values.product_type.flight') },
                        ];
                    case 'carrental':
                        return [
                            { code: 'CARRENTAL', value: this.translate.instant('fields.values.product_type.car') },
                        ];
                    case 'parking':
                        return [
                            { code: 'PARKING', value: this.translate.instant('fields.values.product_type.parking') },
                        ];
                    case 'hotel':
                        return [
                            { code: 'HOTEL', value: this.translate.instant('fields.values.product_type.hotel') },
                        ];
                    default:
                        return [
                            { code: 'FLIGHT', value: this.translate.instant('fields.values.product_type.flight') },
                            { code: 'CARRENTAL', value: this.translate.instant('fields.values.product_type.car') },
                            { code: 'PARKING', value: this.translate.instant('fields.values.product_type.parking') },
                            { code: 'HOTEL', value: this.translate.instant('fields.values.product_type.hotel') }
                        ];
                }
            case 'NEWSLETTER':
                return this.getNewsletterOptions(tenantCode, partner, language || 'FR', value)
            case 'BOOLEAN':
            case 'PRICE':
            case 'NUMBER':
            case 'DATE':
            case 'DATE_TIME':
            case 'TIME':
            case 'PHONE':
            case 'MOBILE':
            case 'TEXT':
            case 'EMAIL':
            case 'TECHNICAL':
            case 'AFFILIATION':
            case 'BOOKING_REFERENCE':
            default:
                return [];
        }
    }

    private async getNewsletterOptions(tenantCode: string, partner: string, language: string, query?: string): Promise<{ code: string; value: string }[]> {
        try {
            const newsletters = await this.newsletterService.getNewslettersUsingGET(tenantCode, language, language, partner).toPromise();
            let result = newsletters.map(newsletter => ({
                code: newsletter.uuid,
                value: newsletter.name
            }));
            if (query) {
                const queryLower = query.toLowerCase();
                result = result.filter(item =>
                    item.value.toLowerCase().includes(queryLower) ||
                    item.code.toLowerCase().includes(queryLower)
                );
            }
            return result;
        } catch(error) {
        console.error('Erreur lors du chargement des newsletters:', error);
        return [];
        }
    }
}
